import store from 'stores/store'
import {
  ComputeBudgetProgram,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { REFERRER_KEY, WRAPPED_SOL_MINT } from 'utils/constants'
import FormatNumericValue from '@components/shared/FormatNumericValue'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton } from '@components/shared/Button'
import {
  NoSymbolIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from '@heroicons/react/20/solid'
import Loading from '@components/loading/Loading'
import { useSortableData } from 'hooks/useSortableData'
import { useQuery } from '@tanstack/react-query'
import {
  fetchLastPriceForMints,
  fetchMetadataForMints,
  goToTokenPage,
  toUiDecimals,
} from 'utils/tokens'
import ImageWithFallback from '@components/image/ImageWithFallback'
import SheenLoader from '@components/loading/SheenLoader'
import { useWallet } from '@solana/wallet-adapter-react'
import { MANIFEST_PROGRAM_ID } from 'utils/limitOrders'
import { sendTxAndConfirm, TransactionError } from 'utils/governance/tools'
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import {
  getSimulationComputeUnits,
  isSetComputeLimitInstruction,
  PLATFORM_FEE_WALLET,
} from 'utils/referrals'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import {
  createBatchUpdateInstruction,
  UiWrapper,
} from '@cks-systems/manifest-sdk'
import {
  createCancelOrderInstruction,
  createSettleFundsInstruction,
} from '@cks-systems/manifest-sdk/dist/cjs/ui_wrapper/instructions'
import { getVaultAddress } from '@cks-systems/manifest-sdk/dist/cjs/utils'
import { notify } from 'utils/notifications'
import { sleep } from 'utils'
import { UiOpenOrder, useOpenOrders } from 'hooks/useOpenOrders'
import { useUnsettledBalances } from 'hooks/useUnsettledBalances'
import { useInvalidateWalletTokens } from 'hooks/useWalletTokens'
import { getFeeEstimate } from 'utils/priorityFee'
import { useRouter } from 'next/router'
import { isError } from 'lodash'
import { usePlausibleChecked } from 'hooks/usePlausibleChecked'
import Tooltip from '@components/shared/Tooltip'
import { TokenMetadataItem } from 'types/token'
import { formatExactPrice } from 'utils/numbers'
import { useTranslation } from 'next-i18next'
import { useReferrerFee } from '@components/partners/useReferrerFee'
import { milliseconds } from 'date-fns'

const metadataCache = new Map<string, TokenMetadataItem[]>()

async function fetchFormattedTableData(
  openOrders: UiOpenOrder[] | undefined,
  orderTokensMeta: TokenMetadataItem[] | undefined,
) {
  if (!openOrders?.length) return []
  const data = []

  const quoteMints = openOrders.map((order) => order.quoteMint.toBase58())
  const quotePrices = await fetchLastPriceForMints(quoteMints)
  for (let i = 0; i < openOrders.length; i++) {
    const order = openOrders[i]
    const {
      baseMint,
      quoteMint,
      tokenPrice: price,
      isBid,
      market,
      clientOrderId: orderId,
      sequenceNumber,
    } = order

    let size = order.numBaseAtoms
    const side = isBid ? 'buy' : 'sell'
    let baseOrderMetaData: TokenMetadataItem | undefined
    let quoteOrderMetaData: TokenMetadataItem | undefined
    let baseProgram = TOKEN_PROGRAM_ID.toBase58()
    let quoteProgram = TOKEN_PROGRAM_ID.toBase58()

    const lastPriceQuote =
      quotePrices.find((p) => p.mint === quoteMint.toBase58())?.price || null

    if (orderTokensMeta && orderTokensMeta?.length) {
      baseOrderMetaData = orderTokensMeta.find(
        (meta) => meta.mint === baseMint.toBase58(),
      )
      quoteOrderMetaData = orderTokensMeta.find(
        (meta) => meta.mint === quoteMint.toBase58(),
      )

      size = toUiDecimals(
        Number(size.toString()),
        baseOrderMetaData?.decimals ?? 0,
      )
    }
    const baseSymbol = baseOrderMetaData?.symbol || 'Unknown'
    const quoteSymbol = quoteOrderMetaData?.symbol || 'Unknown'
    baseProgram =
      baseOrderMetaData?.token_program || TOKEN_PROGRAM_ID.toBase58()
    quoteProgram =
      quoteOrderMetaData?.token_program || TOKEN_PROGRAM_ID.toBase58()
    const quoteValue = price * Number(size)
    const usdValue = lastPriceQuote ? quoteValue * lastPriceQuote : null
    const formattedOrder = {
      marketName: `${baseSymbol}/${quoteSymbol}`,
      baseOrderMetaData,
      quoteOrderMetaData,
      order,
      orderId,
      price,
      side,
      size,
      baseMint,
      market,
      quoteMint,
      isBid,
      value: quoteValue,
      baseProgram,
      quoteProgram,
      usdValue,
      sequenceNumber,
    }
    data.push(formattedOrder)
  }
  return data
}

const OrdersTable = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation(['orders', 'messages'])
  const [cancelId, setCancelId] = useState<number | null>(null)
  const connection = store((s) => s.connection)
  const { publicKey, signTransaction } = useWallet()
  const [localReferrer] = useLocalStorage(REFERRER_KEY)
  const { openOrders, refetchOpenOrders } = useOpenOrders()
  const invalidateWalletTokens = useInvalidateWalletTokens()
  const { refetchUnsettledBalances } = useUnsettledBalances()
  const router = useRouter()
  const plausible = usePlausibleChecked()
  const { referrerFee } = useReferrerFee()

  const metaDataMints = useMemo(() => {
    try {
      if (!openOrders?.length) return []

      const uniqueBaseMints = new Set(
        openOrders.map((order) => order.baseMint.toBase58()),
      )
      const uniqueQuoteMints = new Set(
        openOrders.map((order) => order.quoteMint.toBase58()),
      )

      const combinedMints = new Set([...uniqueBaseMints, ...uniqueQuoteMints])

      return [...combinedMints]
    } catch (e) {
      console.error('error constructing metaDataMints', e)
      return []
    }
  }, [openOrders])

  const { data: orderTokensMeta, isInitialLoading: loadingOrderTokensMeta } =
    useQuery({
      queryKey: ['orders-token-meta', metaDataMints],
      queryFn: () =>
        fetchMetadataForMints(metaDataMints, metadataCache, connection),
      cacheTime: milliseconds({ minutes: 30 }),
      staleTime: milliseconds({ minutes: 30 }),
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: !!metaDataMints?.length,
    })

  const { data: formattedOrders, isLoading: loadingFormattedOrders } = useQuery(
    {
      queryKey: ['formatted-orders', openOrders?.length, orderTokensMeta],
      queryFn: () => fetchFormattedTableData(openOrders, orderTokensMeta),
      cacheTime: milliseconds({ minutes: 30 }),
      staleTime: milliseconds({ minutes: 30 }),
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: !!openOrders?.length && !!orderTokensMeta,
    },
  )

  const { items: tableData } = useSortableData(formattedOrders || [])

  const cancel = useCallback(
    async (
      clientOrderId: number,
      sequenceNumber: number,
      market: PublicKey,
      baseMint: PublicKey,
      quoteMint: PublicKey,
      isBid: boolean,
      localReferrer: string | undefined,
      baseTokenProgram: PublicKey,
      quoteTokenProgram: PublicKey,
    ) => {
      if (!publicKey || !signTransaction) {
        console.warn("Can't cancel order without a wallet")
        return
      }
      if (!isNaN(clientOrderId)) {
        let txid: string | undefined
        setCancelId(clientOrderId)
        const defaultComputeUnit = 400000
        try {
          let refTokenAccountPk = undefined
          const mint = isBid ? quoteMint : baseMint
          const mintTokenProgram = isBid ? quoteTokenProgram : baseTokenProgram

          const quoteMeta = orderTokensMeta?.find(
            (meta) => meta.mint === quoteMint.toBase58(),
          )
          const feeMintIsWSol = quoteMint.equals(WRAPPED_SOL_MINT)

          const hasRef = !!localReferrer
          const fee = await getFeeEstimate(connection)
          let preSolWrapInstructions: TransactionInstruction[] = [
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: fee,
            }),
            ComputeBudgetProgram.setComputeUnitLimit({
              units: defaultComputeUnit,
            }),
          ]
          const postSolWrapInstructions: TransactionInstruction[] = []
          const wrapperAcc = await UiWrapper.fetchFirstUserWrapper(
            connection,
            publicKey,
          )
          if (!wrapperAcc) {
            console.error('No wrapper account found')
            notify({
              type: 'error',
              title: t('errors.no-wrapper'),
            })
            plausible('CancelOrderError', {
              props: {
                error: 'No wrapper account found',
                wallet: publicKey.toString(),
                orderId: clientOrderId,
                market: market.toString(),
                baseMint: baseMint.toString(),
                quoteMint: quoteMint.toString(),
              },
            })
            return
          }

          const traderTokenAccountBase = getAssociatedTokenAddressSync(
            baseMint,
            publicKey,
            true,
            baseTokenProgram,
          )
          const traderTokenAccountQuote = getAssociatedTokenAddressSync(
            quoteMint,
            publicKey,
            true,
            quoteTokenProgram,
          )
          const platformAta = getAssociatedTokenAddressSync(
            quoteMint,
            PLATFORM_FEE_WALLET,
            true,
            quoteTokenProgram,
          )

          const [platformAtaAccount, baseAtaAccount, quoteAtaAccount] =
            await Promise.all([
              connection.getAccountInfo(platformAta),
              connection.getAccountInfo(traderTokenAccountBase),
              connection.getAccountInfo(traderTokenAccountQuote),
            ])

          if (hasRef) {
            const [tokenAccountAddress] = PublicKey.findProgramAddressSync(
              [
                new PublicKey(localReferrer).toBuffer(),
                quoteTokenProgram.toBuffer(),
                quoteMint.toBuffer(),
              ],
              ASSOCIATED_TOKEN_PROGRAM_ID,
            )
            refTokenAccountPk = tokenAccountAddress
            const refVaultAccount =
              await connection.getAccountInfo(tokenAccountAddress)
            const refVaultExists =
              refVaultAccount && refVaultAccount.lamports > 0
            if (!refVaultExists) {
              const referrerVaultIx =
                createAssociatedTokenAccountIdempotentInstruction(
                  publicKey,
                  tokenAccountAddress,
                  new PublicKey(localReferrer),
                  quoteMint,
                  quoteTokenProgram,
                )
              preSolWrapInstructions.push(referrerVaultIx)
            }
          }

          const doesPlatformAtaExist =
            platformAtaAccount && platformAtaAccount.lamports > 0

          const doesBaseAtaExist = baseAtaAccount && baseAtaAccount.lamports > 0
          const doesQuoteAtaExist =
            quoteAtaAccount && quoteAtaAccount.lamports > 0

          if (!doesPlatformAtaExist) {
            const platformAtaCreateIx =
              createAssociatedTokenAccountIdempotentInstruction(
                publicKey,
                platformAta,
                PLATFORM_FEE_WALLET,
                quoteMint,
                quoteTokenProgram,
              )
            preSolWrapInstructions.push(platformAtaCreateIx)
          }
          if (!doesQuoteAtaExist) {
            const quoteAtaCreateIx =
              createAssociatedTokenAccountIdempotentInstruction(
                publicKey,
                traderTokenAccountQuote,
                publicKey,
                quoteMint,
                quoteTokenProgram,
              )
            preSolWrapInstructions.push(quoteAtaCreateIx)
          }
          if (!doesBaseAtaExist) {
            const baseAtaCreateIx =
              createAssociatedTokenAccountIdempotentInstruction(
                publicKey,
                traderTokenAccountBase,
                publicKey,
                baseMint,
                baseTokenProgram,
              )
            preSolWrapInstructions.push(baseAtaCreateIx)
          }
          const cancelOrderIx = createCancelOrderInstruction(
            {
              wrapperState: wrapperAcc.pubkey,
              owner: publicKey,
              traderTokenAccount: traderTokenAccountBase,
              market: market,
              vault: getVaultAddress(market, mint),
              mint: mint,
              systemProgram: SystemProgram.programId,
              tokenProgram: mintTokenProgram,
              manifestProgram: MANIFEST_PROGRAM_ID,
            },
            {
              params: { clientOrderId },
            },
          )
          preSolWrapInstructions.push(cancelOrderIx)
          const settleOrderIx = createSettleFundsInstruction(
            {
              wrapperState: wrapperAcc.pubkey,
              owner: publicKey,
              market: market,
              manifestProgram: MANIFEST_PROGRAM_ID,
              traderTokenAccountBase: traderTokenAccountBase,
              traderTokenAccountQuote: traderTokenAccountQuote,
              vaultBase: getVaultAddress(market, baseMint),
              vaultQuote: getVaultAddress(market, quoteMint),
              mintBase: baseMint,
              mintQuote: quoteMint,
              tokenProgramBase: baseTokenProgram,
              tokenProgramQuote: quoteTokenProgram,
              platformTokenAccount: platformAta,
              referrerTokenAccount: refTokenAccountPk,
            },
            {
              params: {
                feeMantissa: 10 ** 9 * (refTokenAccountPk ? 0.0009 : 0.001),
                platformFeePercent: 100 - referrerFee,
              },
            },
          )

          postSolWrapInstructions.push(settleOrderIx)

          if (baseMint.equals(WRAPPED_SOL_MINT)) {
            const solTransferIx = createCloseAccountInstruction(
              traderTokenAccountBase,
              publicKey,
              publicKey,
            )
            postSolWrapInstructions.push(solTransferIx)
          }
          if (quoteMint.equals(WRAPPED_SOL_MINT)) {
            const solTransferIx = createCloseAccountInstruction(
              traderTokenAccountQuote,
              publicKey,
              publicKey,
            )
            postSolWrapInstructions.push(solTransferIx)
          }

          const units = await getSimulationComputeUnits(
            connection,
            [...preSolWrapInstructions, ...postSolWrapInstructions],
            publicKey,
            [],
          )
          //replace compute unit with dynamic value
          preSolWrapInstructions = [
            ...preSolWrapInstructions.map((ix) => {
              if (isSetComputeLimitInstruction(ix)) {
                return ComputeBudgetProgram.setComputeUnitLimit({
                  units: units || defaultComputeUnit,
                })
              }
              return ix
            }),
          ]

          const block = await connection.getLatestBlockhash('finalized')

          let vtx = new VersionedTransaction(
            new TransactionMessage({
              payerKey: publicKey,
              instructions: [
                ...preSolWrapInstructions,
                ...postSolWrapInstructions,
              ],
              recentBlockhash: block.blockhash,
            }).compileToV0Message(),
          )

          //TODO more errors handling generic
          const simulation = await connection.simulateTransaction(vtx)
          const simulationLogs = simulation.value.logs?.toString()
          if (
            simulation.value.err &&
            simulationLogs?.includes('Error: insufficient funds')
          ) {
            const feeAmount = parseInt(
              simulationLogs.match(/fee_atoms:(\d+)/)?.[1] || '0',
              10,
            )

            if (feeMintIsWSol) {
              const solTransferIx = SystemProgram.transfer({
                fromPubkey: publicKey,
                toPubkey: traderTokenAccountQuote,
                lamports: feeAmount,
              })
              const syncNative = createSyncNativeInstruction(
                traderTokenAccountQuote,
              )
              preSolWrapInstructions.push(solTransferIx, syncNative)
              const block = await connection.getLatestBlockhash('finalized')
              vtx = new VersionedTransaction(
                new TransactionMessage({
                  payerKey: publicKey,
                  instructions: [
                    ...preSolWrapInstructions,
                    ...postSolWrapInstructions,
                  ],
                  recentBlockhash: block.blockhash,
                }).compileToV0Message(),
              )
            } else {
              notify({
                type: 'error',
                title: t('errors.insufficient-funds', {
                  amount: toUiDecimals(feeAmount, quoteMeta?.decimals || 6),
                  symbol: quoteMeta?.symbol || quoteMint.toBase58(),
                }),
              })
              return
            }
          }
          const signed = await signTransaction(vtx)
          txid = await sendTxAndConfirm(connection, signed, block, {
            transactionSent: t('messages:transaction-sent'),
            waitingForConfirmation: t('messages:waiting-for-confirmation'),
          })
          notify({
            title: t('messages:transaction-confirmed'),
            type: 'success',
            txid,
          })
          await sleep(1000)
          void invalidateWalletTokens()
          void refetchUnsettledBalances()
          void refetchOpenOrders()
        } catch (e: unknown) {
          console.error('error cancelling order', e)
          notify({
            title: t('messages:errors.transaction-error'),
            description:
              e != null
                ? e instanceof TransactionError || isError(e)
                  ? e.message
                  : String(e)
                : t('messages:errors.general-try-again'),
            type: 'error',
            txid:
              txid != null ? txid : e instanceof TransactionError ? e.txid : '',
          })
          plausible('CancelOrderError', {
            props: {
              error: String(e),
              wallet: String(publicKey),
              txid,
              orderId: clientOrderId,
              market: market.toString(),
              baseMint: baseMint.toString(),
              quoteMint: quoteMint.toString(),
            },
          })
        } finally {
          setCancelId(null)
        }
      } else {
        //backup if something is wrong or someone used other dapp to interact with manifest

        const defaultComputeUnit = 400000
        const fee = await getFeeEstimate(connection)
        const instructions = [
          ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: fee,
          }),
          ComputeBudgetProgram.setComputeUnitLimit({
            units: defaultComputeUnit,
          }),
        ]

        const cancelIx = createBatchUpdateInstruction(
          {
            payer: publicKey,
            market: market,
          },
          {
            params: {
              cancels: [
                {
                  orderSequenceNumber: sequenceNumber,
                  orderIndexHint: null,
                },
              ],
              orders: [],
              traderIndexHint: null,
            },
          },
        )
        instructions.push(cancelIx)
        const block = await connection.getLatestBlockhash('finalized')

        const vtx = new VersionedTransaction(
          new TransactionMessage({
            payerKey: publicKey,
            instructions: [...instructions],
            recentBlockhash: block.blockhash,
          }).compileToV0Message(),
        )
        const signed = await signTransaction(vtx)
        const txid = await sendTxAndConfirm(connection, signed, block, {
          transactionSent: t('messages:transaction-sent'),
          waitingForConfirmation: t('messages:waiting-for-confirmation'),
        })
        notify({
          title: t('messages:transaction-confirmed'),
          type: 'success',
          txid,
        })
        await sleep(1000)
        void invalidateWalletTokens()
        void refetchUnsettledBalances()
        void refetchOpenOrders()
      }
    },
    [
      connection,
      invalidateWalletTokens,
      orderTokensMeta,
      plausible,
      publicKey,
      referrerFee,
      refetchOpenOrders,
      refetchUnsettledBalances,
      signTransaction,
      t,
    ],
  )
  useEffect(() => {
    void refetchOpenOrders()
  }, [refetchOpenOrders])

  return openOrders && openOrders?.length ? (
    <div>
      {loadingOrderTokensMeta || loadingFormattedOrders
        ? Array.from({ length: 6 }, (_, i) => (
            <SheenLoader className="flex flex-1 rounded-none" key={i}>
              <div
                className={`h-12 w-full ${
                  i % 2 ? 'bg-th-bkg-2' : 'bg-th-bkg-3'
                }`}
              />
            </SheenLoader>
          ))
        : tableData.map((data, i) => {
            const {
              baseOrderMetaData,
              quoteOrderMetaData,
              baseMint,
              quoteMint,
              orderId,
              price,
              side,
              size,
              market,
              isBid,
              baseProgram,
              quoteProgram,
              value,
              usdValue,
              sequenceNumber,
            } = data
            const { symbol: baseSymbol, image_url: baseImageUrl } =
              baseOrderMetaData ?? {}
            const { symbol: quoteSymbol, image_url: quoteImageUrl } =
              quoteOrderMetaData ?? {}

            return (
              <div
                className="default-transition -mx-3 flex items-center gap-3 rounded-xl px-3 hover:bg-th-bkg-3 focus:outline-none"
                key={`${orderId}${i}`}
              >
                <button
                  className="flex w-full items-center justify-between py-3"
                  onClick={() => {
                    goToTokenPage(
                      baseMint.toBase58(),
                      router,
                      undefined,
                      baseOrderMetaData,
                      quoteOrderMetaData,
                    )
                    if (onClick) {
                      onClick()
                    }
                  }}
                >
                  <div>
                    {loadingOrderTokensMeta ? (
                      <SheenLoader>
                        <div className="h-4 w-24 bg-th-bkg-2" />
                      </SheenLoader>
                    ) : (
                      <>
                        <div className="mb-1 flex items-center space-x-2">
                          {baseImageUrl ? (
                            <ImageWithFallback
                              src={baseImageUrl}
                              height={16}
                              width={16}
                              alt={`${baseSymbol} token logo`}
                            />
                          ) : (
                            <QuestionMarkCircleIcon className="size-4 text-th-fgd-4" />
                          )}
                          <div>
                            <p
                              className={`text-left font-body text-xs xl:text-sm ${
                                side === 'buy' ? 'text-th-up' : 'text-th-down'
                              }`}
                            >
                              {side.toUpperCase()}{' '}
                              <FormatNumericValue value={size.toString()} />{' '}
                              {baseSymbol}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {quoteImageUrl ? (
                            <ImageWithFallback
                              src={quoteImageUrl}
                              height={16}
                              width={16}
                              alt={`${quoteSymbol} token logo`}
                            />
                          ) : (
                            <QuestionMarkCircleIcon className="size-4 text-th-fgd-4" />
                          )}
                          <div>
                            <p
                              className={`text-left font-body text-xs xl:text-sm ${
                                side !== 'buy' ? 'text-th-up' : 'text-th-down'
                              }`}
                            >
                              {side === 'buy' ? 'SELL' : 'BUY'}{' '}
                              <FormatNumericValue value={value} /> {quoteSymbol}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <p className="mb-0.5 text-right text-xs text-th-fgd-2 xl:mb-1 xl:text-sm">
                      {t('table.price', {
                        price: formatExactPrice(price),
                        quoteSymbol,
                        baseSymbol,
                      })}
                    </p>
                    {usdValue ? (
                      <p className="text-xs xl:text-sm">
                        $
                        <FormatNumericValue value={usdValue} />
                      </p>
                    ) : null}
                  </div>
                </button>
                <Tooltip content={t('table.cancel')}>
                  <IconButton
                    className="bg-th-down !text-th-button-text md:hover:bg-th-down-dark"
                    disabled={cancelId === orderId}
                    onClick={() =>
                      cancel(
                        Number(orderId),
                        sequenceNumber,
                        market,
                        baseMint,
                        quoteMint,
                        isBid,
                        localReferrer?.wallet,
                        new PublicKey(baseProgram),
                        new PublicKey(quoteProgram),
                      )
                    }
                    size="small"
                  >
                    {cancelId === Number(orderId) ? (
                      <Loading className="size-4" />
                    ) : (
                      <TrashIcon className="size-4" />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            )
          })}
    </div>
  ) : (
    <div className="flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
      <div className="flex flex-col items-center">
        <NoSymbolIcon className="mb-2 size-5 text-th-fgd-4" />
        <p className="mb-1">{t('table.no-orders')}</p>
      </div>
    </div>
  )
}

export default OrdersTable
